import React, { useEffect, useMemo, useState } from "react";
import { Form, Input, Upload } from "antd";
import type { FormInstance } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { PaymentMethodType } from "types/paymentMethodTypes";

type Props = {
  form: FormInstance;
  paymentMethods: PaymentMethodType[];
  paymentMethodIdField?: string; 
  i18nBase?: string; 
  accept?: string; 
  maxCount?: number; 
  maxSizeMB?: string | number;
  hasExistingTransferProof?: boolean;
};

const PAYMENT_METHOD_TYPES = {
  CASH: 'cash',
  CHECK: 'bank_check',
  BANK_TRANSFER: 'bank_transfer',
} as const;

const PaymentMethodExtraFields: React.FC<Props> = ({
  form,
  paymentMethods,
  paymentMethodIdField = "paymentMethodId",
  i18nBase = "paymentMethod",
  accept = ".pdf,.jpg,.jpeg,.png",
  maxCount = 1,
  maxSizeMB,
  hasExistingTransferProof = false,
}) => {
  const { t } = useTranslation();
  const [transferProofList, setTransferProofList] = useState<any[]>([]);

  const paymentMethodId = Form.useWatch(paymentMethodIdField, form);

  const selected = useMemo(() => {
    return paymentMethods?.find((m) => m.id === paymentMethodId);
  }, [paymentMethods, paymentMethodId]);

  const flags = useMemo(() => {
    if (!selected?.type) {
      return { 
        isCheque: false, 
        isTransfer: false, 
        showBankName: false 
      };
    }

    return {
      isCheque: selected.type === PAYMENT_METHOD_TYPES.CHECK,
      isTransfer: selected.type === PAYMENT_METHOD_TYPES.BANK_TRANSFER,
      showBankName: selected.type === PAYMENT_METHOD_TYPES.CHECK,
    };
  }, [selected?.type]);

  const prevPaymentMethodId = React.useRef<string | undefined>(undefined);
  
  useEffect(() => {
    if (prevPaymentMethodId.current === undefined) {
      prevPaymentMethodId.current = paymentMethodId;
      return;
    }

    if (prevPaymentMethodId.current === paymentMethodId) return;

    prevPaymentMethodId.current = paymentMethodId;

    if (!paymentMethodId) {
      form.setFieldsValue({
        bankName: null,
        chequeReference: null,
        transferProof: null,
      });
      setTransferProofList([]);
      return;
    }

    if (!flags.isCheque) {
      form.setFieldsValue({ chequeReference: null });
    }
    if (!flags.isTransfer) {
      form.setFieldsValue({ transferProof: null });
      setTransferProofList([]);
    }
    if (!flags.showBankName) {
      form.setFieldsValue({ bankName: null });
    }
  }, [paymentMethodId, flags.isCheque, flags.isTransfer, flags.showBankName, form]);

  const handleTransferProofChange = ({ file }: any) => {
    if (file) {
      setTransferProofList([file]);
      form.setFieldsValue({ transferProof: file });
    }
  };

  if (!paymentMethodId) return null;

  return (
    <>
      {/* {flags.showBankName && ( */}
        <Form.Item
          hidden={!flags.showBankName}
          label={t(`${i18nBase}.form.bank_name`)}
          name="bankName"
          rules={[
            {
              required: flags.showBankName,
              message: t(`${i18nBase}.validation.please_enter_bank_name`),
            },
          ]}
        >
          <Input placeholder={t(`${i18nBase}.form.bank_name`)} />
        </Form.Item>
      {/* )} */}

      {/* {flags.isCheque && ( */}
        <Form.Item
          hidden={!flags.isCheque}
          label={t(`${i18nBase}.form.cheque_reference`)}
          name="chequeReference"
          rules={[
            {
              required: flags.isCheque,
              message: t(`${i18nBase}.validation.please_enter_cheque_reference`),
            },
          ]}
        >
          <Input placeholder={t(`${i18nBase}.form.cheque_reference`)} />
        </Form.Item>
      {/* )} */}

      {/* {flags.isTransfer && ( */}
        <Form.Item
          hidden={!flags.isTransfer}
          label={t(`${i18nBase}.form.transfer_proof`)}
          name="transferProof"
          rules={[
            {
              required: flags.isTransfer && !hasExistingTransferProof,
              message: t(`${i18nBase}.validation.please_upload_transfer_proof`),
            },
          ]}
        >
          <Upload.Dragger
            beforeUpload={() => false}
            onChange={handleTransferProofChange}
            accept={accept}
            maxCount={maxCount}
            fileList={transferProofList}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>

            {!!maxSizeMB && (
              <p className="ant-upload-text">
                {t("max_file_size_to_upload", { maxSize: maxSizeMB })}
              </p>
            )}

            <p className="ant-upload-hint">
              {t("click_to_upload", { format: accept.replaceAll(".", "") })}
            </p>
          </Upload.Dragger>
        </Form.Item>
      {/* )} */}
    </>
  );
};

export default PaymentMethodExtraFields;